import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, ClockIcon } from "lucide-react";
import { BrutalButton } from "../../ui/BrutalButton";
import { spring } from "../../../utils/motion";
interface WelcomeStepProps {
  name: string;
  onStart: () => void;
}
const LOGO_SRC = "https://cdn.magicpatterns.com/uploads/mZTqWzu3jsAmPwWT8eCXNP/polished-app-icon-logo--a-large-rounded-square-ico.svg";
const agenda = [{
  title: "Your languages",
  text: "What you speak and what you want to learn"
}, {
  title: "Your reasons",
  text: "So examples and topics feel relevant"
}, {
  title: "Where you're starting",
  text: "A few taps, plus an optional quick check"
}, {
  title: "Your rhythm",
  text: "Minutes per day and when to nudge you"
}];
export function WelcomeStep({
  name,
  onStart
}: WelcomeStepProps) {
  const reduce = useReducedMotion() ?? false;
  return <div className="grid items-center gap-10 md:grid-cols-[1.1fr_1fr] md:gap-16" data-id="element-1084" data-mp="3o4hvlbhnmpr1">
      <div data-id="element-1085" data-mp="2kv57lcgoye2j">
        <motion.img src={LOGO_SRC} alt="" aria-hidden="true" className="-ml-4 h-28 w-28 md:h-32 md:w-32" initial={reduce ? false : {
        opacity: 0,
        scale: 0.96,
        rotate: -6
      }} animate={{
        opacity: 1,
        scale: 1,
        rotate: -4
      }} transition={spring.bouncy} draggable={false} data-id="element-1086" data-mp="2kun4pwzix1ln" />
        <h1 className="mt-4 text-[34px] font-bold leading-[1.05] tracking-[-0.02em] text-ink md:text-[52px]" data-id="element-1087" data-mp="2fu8woexx9rnm">
          Welcome, {name}.
          <br data-id="element-1088" data-mp="26v3yj9q9w0bz" />
          Let's make this course yours.
        </h1>
        <p className="mt-4 max-w-[46ch] text-[17px] leading-relaxed text-ink-2" data-id="element-1089" data-mp="26xgpudhx7n1p">
          Answer a few quick questions and we'll set your starting point, focus and daily goal — no generic Unit 1 if you already know the basics.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-1090" data-mp="1j59muvhowhtb">
          <BrutalButton size="lg" onClick={onStart} data-id="element-1091" data-mp="1j85k0hlpr2gp">
            Let's go <ArrowRightIcon className="h-5 w-5" data-id="element-1092" data-mp="1o3kx37bwcuc2" />
          </BrutalButton>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-ink-2" data-id="element-1093" data-mp="3publnk4wj98l">
            <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1094" data-mp="3l2tkauyyv6n3" /> About 2 minutes
          </span>
        </div>
      </div>

      <ol className="relative space-y-5 border-l-2 border-dashed border-line/25 pl-7" aria-label="What we'll ask" data-id="element-1095" data-mp="3g5a77ye08zwq">
        {agenda.map((a, i) => <motion.li key={a.title} className="relative" initial={reduce ? false : {
        opacity: 0,
        x: 12
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        ...spring.smooth,
        delay: reduce ? 0 : 0.1 + i * 0.05
      }} data-id="element-1096" data-mp="3c0u3a2nyc2k5" data-mpi={i}>
            <span className="absolute -left-[41px] top-0 grid h-7 w-7 place-items-center rounded-full border-3 border-line bg-surface text-xs font-bold text-ink" data-id="element-1097" data-mp="3l0mdmppkp2k">
              {i + 1}
            </span>
            <p className="text-[15px] font-bold text-ink" data-id="element-1098" data-mp="2oedqunsqkd4p">{a.title}</p>
            <p className="text-sm text-ink-2" data-id="element-1099" data-mp="2jd6l0ua5lo0o">{a.text}</p>
          </motion.li>)}
        <li className="text-sm text-ink-2" data-id="element-1100" data-mp="yx04zheqgsy7">You can change any answer later in Settings.</li>
      </ol>
    </div>;
}